import { stringify as queryStringify } from 'querystring';
import { AssertOkError, AssertStatusCode, AuthenticationError } from './errors/types';
import { reloadHandler, redirectHandler } from './errors';

const stringify = require('json-stringify-safe');

export interface Params {
    [key: string]: any;
}

export const createRestClient = (api: string, baseUrl: string) => {
    const request = (
        path: string,
        method: string,
        query: Params = {},
        headers: Record<string, string> = {},
        payload: Params | undefined = undefined,
    ) => {
        const queryParams = queryStringify(query);
        const fullUrl = `${api}${baseUrl}${path}${queryParams !== '' ? `?${queryParams}` : ''}`;

        return fetch(fullUrl, {
            method,
            credentials: 'same-origin',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                CLIENT_TYPE: 'web',
                ...(payload ? { 'Content-Type': 'application/json' } : {}),
                ...headers,
            },
            body: payload ? stringify(payload) : undefined,
        });
    };
    return {
        get(
            path: string,
            query: Params = {},
            headers: Record<string, string> = {},
        ): Promise<Response> {
            return request(path, 'GET', query, headers);
        },
        post(
            path: string,
            query: Params = {},
            payload: Params = {},
            headers: Record<string, string> = {},
        ): Promise<Response> {
            return request(path, 'POST', query, headers, payload);
        },
        put(
            path: string,
            query: Params = {},
            payload: Params = {},
            headers: Record<string, string> = {},
        ): Promise<Response> {
            return request(path, 'PUT', query, headers, payload);
        },
        patch(
            path: string,
            query: Params = {},
            payload: Params = {},
            headers: Record<string, string> = {},
        ): Promise<Response> {
            return request(path, 'PATCH', query, headers, payload);
        },
        delete(
            path: string,
            query: Params = {},
            headers: Record<string, string> = {},
        ): Promise<Response> {
            return request(path, 'DELETE', query, headers);
        },
    };
};

export type RestClient = ReturnType<typeof createRestClient>;

export const assertAndHandle40xLink = (response: Response) => {
    try {
        if (response.status !== 401 && response.status !== 403) {
            return response;
        }

        throw new AuthenticationError(response.status, response.headers.get('Link'));
    } catch (err) {
        if (err instanceof AuthenticationError && err.redirectTarget) {
            redirectHandler(err.redirectTarget);
        } else {
            reloadHandler();
        }

        throw err;
    }
};

export const assertOk = () => (response: Response): Response => {
    if (!response.ok) {
        throw new AssertOkError('Request was unsuccessful');
    }

    return response;
};

export const assertStatusCode = (codes: number[]) => (response: Response): Response => {
    if (codes.indexOf(response.status) === -1) {
        throw new AssertStatusCode(
            response.status,
            `Expected status codes ${JSON.stringify(codes)} but got ${response.status}`,
        );
    }

    return response;
};
